import React, { useState } from 'react';
import { Modal, Button, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { Camera, Image, Video, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { capturePhotoWithCamera, selectPhotoFromGallery, selectOrRecordVideo, compressImageFile } from '../../utils/mediaPicker';
import './MobileMediaPickerModal.css';

const MobileMediaPickerModal = ({
  show,
  onHide,
  onFileSelected,
  mediaType = 'image', // 'image' | 'video' | 'both'
  title = 'Select Media',
  maxSizeMB = 50
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [originalSizeStr, setOriginalSizeStr] = useState('');
  const [compressedSizeStr, setCompressedSizeStr] = useState('');

  const handleClear = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setErrorMsg('');
    setOriginalSizeStr('');
    setCompressedSizeStr('');
  };

  const processFile = async (rawFile) => {
    if (!rawFile) return;
    setErrorMsg('');
    const origMb = (rawFile.size / 1024 / 1024).toFixed(2);
    setOriginalSizeStr(`${origMb} MB`);

    if (rawFile.size > maxSizeMB * 1024 * 1024) {
      setErrorMsg(`File size (${origMb} MB) exceeds maximum allowed limit of ${maxSizeMB} MB.`);
      return;
    }

    if (rawFile.type.startsWith('image/')) {
      setCompressing(true);
      try {
        const compressed = await compressImageFile(rawFile);
        const compMb = (compressed.size / 1024 / 1024).toFixed(2);
        setCompressedSizeStr(`${compMb} MB`);
        setSelectedFile(compressed);
        setPreviewUrl(URL.createObjectURL(compressed));
      } catch (err) {
        setSelectedFile(rawFile);
        setPreviewUrl(URL.createObjectURL(rawFile));
      } finally {
        setCompressing(false);
      }
    } else {
      // Video
      setCompressedSizeStr(`${origMb} MB`);
      setSelectedFile(rawFile);
      setPreviewUrl(URL.createObjectURL(rawFile));
    }
  };

  const handleCameraClick = async () => {
    try {
      if (mediaType === 'video') {
        const videoFile = await selectOrRecordVideo('camera');
        if (videoFile) await processFile(videoFile);
      } else {
        const photoFile = await capturePhotoWithCamera();
        if (photoFile) await processFile(photoFile);
      }
    } catch (err) {
      setErrorMsg('Failed to open camera. Please try selecting from gallery.');
    }
  };

  const handleGalleryClick = async () => {
    try {
      if (mediaType === 'video') {
        const videoFile = await selectOrRecordVideo('gallery');
        if (videoFile) await processFile(videoFile);
      } else {
        const photoFile = await selectPhotoFromGallery();
        if (photoFile) await processFile(photoFile);
      }
    } catch (err) {
      setErrorMsg('Failed to open gallery.');
    }
  };

  const handleConfirm = () => {
    if (!selectedFile) return;
    onFileSelected(selectedFile);
    handleClear();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered className="mobile-media-picker-modal">
      <Modal.Header closeButton className="bg-dark text-white border-secondary">
        <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
          {mediaType === 'video' ? <Video size={20} className="text-danger" /> : <Image size={20} className="text-danger" />}
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-dark text-white py-4 px-3">
        {errorMsg && (
          <Alert variant="danger" className="d-flex align-items-center gap-2 py-2 mb-3">
            <AlertCircle size={18} />
            <span className="small">{errorMsg}</span>
          </Alert>
        )}

        {!selectedFile ? (
          <div className="d-flex flex-column gap-3">
            <button
              type="button"
              className="media-source-btn d-flex align-items-center justify-content-center gap-3 py-3 rounded-3 btn btn-outline-danger"
              onClick={handleCameraClick}
            >
              <Camera size={26} />
              <div className="text-start">
                <div className="fw-bold">Take {mediaType === 'video' ? 'Video' : 'Photo'} with Camera</div>
                <div className="small text-muted">Use native device camera</div>
              </div>
            </button>

            <button
              type="button"
              className="media-source-btn d-flex align-items-center justify-content-center gap-3 py-3 rounded-3 btn btn-outline-secondary text-white"
              onClick={handleGalleryClick}
            >
              <Image size={26} />
              <div className="text-start">
                <div className="fw-bold">Select from Gallery / Files</div>
                <div className="small text-muted">Browse device photo & video library</div>
              </div>
            </button>
          </div>
        ) : (
          <div className="text-center">
            {compressing ? (
              <div className="py-4">
                <Spinner animation="border" variant="danger" className="mb-2" />
                <div className="small text-light">Optimizing media quality & size...</div>
              </div>
            ) : (
              <>
                <div className="preview-container mb-3 position-relative rounded overflow-hidden bg-black" style={{ maxHeight: '240px' }}>
                  {selectedFile.type.startsWith('video/') ? (
                    <video src={previewUrl} controls style={{ width: '100%', maxHeight: '240px', objectFit: 'contain' }} />
                  ) : (
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: '240px', objectFit: 'contain' }} />
                  )}
                  <button
                    type="button"
                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 rounded-circle p-1"
                    onClick={handleClear}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="bg-secondary bg-opacity-25 rounded p-2 mb-3 text-start small">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Original Size:</span>
                    <span className="fw-bold text-light">{originalSizeStr}</span>
                  </div>
                  {compressedSizeStr && originalSizeStr !== compressedSizeStr && (
                    <div className="d-flex justify-content-between text-success">
                      <span>Optimized Size:</span>
                      <span className="fw-bold">{compressedSizeStr}</span>
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" className="flex-fill text-white" onClick={handleClear}>
                    Retake / Change
                  </Button>
                  <Button variant="danger" className="flex-fill fw-bold" onClick={handleConfirm}>
                    <CheckCircle size={18} className="me-1" /> Use Media
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MobileMediaPickerModal;
